import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Divider,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from "@mui/material";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, BarController, LineElement, LineController, PointElement, Title, Tooltip, Legend } from "chart.js";
import { Chart } from "react-chartjs-2";
import { ProducerCommissionCompanyOverride } from "../../interfaces/DTO/commissions/producerCommission";
import { formatCurrency, formatHandlerCode } from "../../utils/namesFormater";
import { colorHandlerCode } from "../../utils/colorEnumFormatter";
import { ChartContainer, buildChartOptions, buildBarDataset, buildLineDataset } from "../../shared/UIParts/chartUtils";

ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, LineElement, LineController, PointElement, Title, Tooltip, Legend);

interface Props {
  overrides: ProducerCommissionCompanyOverride[];
}

const CommissionOverridesChart: React.FC<Props> = ({ overrides }) => {
  const [xAxis, setXAxis] = useState<"max_value" | "saving_fee">("saving_fee");
  const [chartType, setChartType] = useState<"bar" | "line">("bar");

  const grouped = useMemo(() => {
    const result: Record<string, ProducerCommissionCompanyOverride[]> = {};
    for (const row of overrides) {
      if (!result[row.handler_code]) result[row.handler_code] = [];
      result[row.handler_code].push(row);
    }
    return result;
  }, [overrides]);

  const chartData = useMemo(() => {
    const labelsSet = new Set<string>();
    const allBuckets: Record<string, number>[] = [];

    Object.values(grouped).forEach(rows => {
      const sorted = [...rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      sorted.forEach(r => {
        const label = `${xAxis === "max_value" ? r.max_value : r.saving_fee}`;
        if (label === 'undefined' || label === 'null') return;
        labelsSet.add(label);
      });
    });

    const labels = Array.from(labelsSet).sort((a, b) => parseFloat(a) - parseFloat(b));

    const datasets = Object.entries(grouped).map(([handlerCode, rows]) => {
      const sorted = [...rows].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      const dataMap = new Map<string, number>();

      sorted.forEach(r => {
        const label = `${xAxis === "max_value" ? (r.max_value || 0) : (r.saving_fee || 0)}`;
        const value = xAxis === "max_value" ? (r.saving_fee || 0) : (r.max_value || 0);
        dataMap.set(label, value);
      });

      const data = labels.map(label => {
        if (dataMap.has(label)) return dataMap.get(label);
        return chartType === "bar" ? undefined : null;
      });

      const baseDataset = {
        label: formatHandlerCode(handlerCode),
        data,
        backgroundColor: colorHandlerCode(handlerCode),
        borderColor: colorHandlerCode(handlerCode)
      };
      return chartType === "bar" ? buildBarDataset(baseDataset) : buildLineDataset(baseDataset);
    });

    return { labels, datasets };
  }, [grouped, xAxis, chartType]);

  const chartOptions = useMemo(() =>
    buildChartOptions({
      isLineChart: chartType === "line",
      isStacked: false,
      formatValue: xAxis === "max_value" ? (v: number) => `${v}%` : formatCurrency,
      tooltip: {
        filter: (tooltipItem: any) => tooltipItem.raw !== undefined
      }
    }),
  [xAxis, chartType]);

  return (
    <Box sx={{ mt:3}}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">התפלגות עמלות לפי {xAxis === "max_value" ? "תקרה" : "דמי ניהול"}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant={chartType === "bar" ? "contained" : "outlined"} onClick={() => setChartType("bar")}>Bar</Button>
          <Divider orientation="vertical" flexItem />
          <Button variant={chartType === "line" ? "contained" : "outlined"} onClick={() => setChartType("line")}>Line</Button>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant={xAxis === "max_value" ? "contained" : "outlined"} onClick={() => setXAxis("max_value")}>סכום →</Button>
          <Divider orientation="vertical" flexItem />
          <Button variant={xAxis === "saving_fee" ? "contained" : "outlined"} onClick={() => setXAxis("saving_fee")}>דמי ניהול →</Button>
        </Stack>
      </Stack>

      <ChartContainer height={300}>
        <Chart type={chartType} data={chartData} options={chartOptions} />
      </ChartContainer>
    </Box>
  );
};

export default CommissionOverridesChart;
